import { useEffect, useState } from "react";
import DashboardLayout from "../../../layoutcontainers/DashboardLayout";
import Table from "../../../components/Table";
import MaterialTypography from "../../../components/MaterialTypography";
import { Card, CardHeader, Stack } from "@mui/material";
import { useNavigate } from "react-router-dom";
import MaterialButton from "../../../components/MaterialButton";
import AddIcon from "@mui/icons-material/Add";
import { FileUpload, FilterAlt } from "@mui/icons-material";
import { deleteData, fetchData } from "../../../services";
import { useDispatch, useSelector } from "react-redux";
import {
  setCurrentPage,
  setRowsPerPage,
  setTotalPages,
} from "./store/motorBodyStyleStore";
import { useLoading } from "../../../components/Loading";
import BulkUpload from "../../../components/blukUplod";
import { useSnackbar } from "../../../context/popup";
import BodyStyleListFilter from "./components/BodyStyleListFilter.js";

const MotorBodyStyleList = () => {

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { setCircleLoading } = useLoading();
  const { showSnackbar } = useSnackbar();

  const currentPage = useSelector(
    (state) => state?.motorBodyStyleStore?.currentPage
  );
  const rowsPerPage = useSelector(
    (state) => state?.motorBodyStyleStore?.rowsPerPage
  );
  const totalPages = useSelector(
    (state) => state?.motorBodyStyleStore?.totalPages
  );

  const [isRefresh, setIsRefresh] = useState(false);
  const [openUploadFileDialog, setOpenUploadFileDialog] = useState(false);
  const [bodyStyleData, setBodyStyleData] = useState([]);
  const [search, setSearch] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const [filterData, setFilterData] = useState({
    bodyTypeId: "",
    isActive: "",
  });

  const fetchBodyStyleData = async () => {
    setCircleLoading(true);
    try {
      let url = `/reference/motor/bodystyles?page=${currentPage}&limit=${rowsPerPage}&searchTerm=${search}&bodyTypeId=${filterData?.bodyTypeId}`;

      //for active and inactive status
      if (filterData.isActive !== "") {
        url += `&isActive=${filterData.isActive}`;
      }

      const response = await fetchData(url);
      if (response?.success) {
        setBodyStyleData(response?.data?.items);
        dispatch(setTotalPages(response?.data?.totalPages));
      } else {
        showSnackbar("Error Fetching Body Style Data", "error");
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      showSnackbar(error?.response?.data?.message || 'Network Error', "error");
    } finally {
      setCircleLoading(false);
    }
  };

  useEffect(() => {
    fetchBodyStyleData();
  }, [isRefresh, currentPage, rowsPerPage, search]);

  const columns = [
    {
      key: "sno",
      label: "S.No",
      renderCell: (row) => row.sno,
    },
    {
      key: "name",
      label: "Body Style",
      renderCell: (row) => row.name,
    },
    {
      key: "motorBodyType",
      label: "Body Type",
      renderCell: (row) => row.motorBodyType,
    },
    {
      key: "createdAt",
      label: "Created At",
      renderCell: (row) => row.createdAt,
    },
    {
      key: "isActive",
      label: "Status",
      renderCell: (row) => (
        <span>
          {row.isActive ? (
            <span style={{ color: "green", fontWeight: "bold" }}>Active</span>
          ) : (
            <span style={{ color: "red", fontWeight: "bold" }}>Inactive</span>
          )}
        </span>
      ),
    },
  ];

  const rows = bodyStyleData.map((bodyStyle, index) => ({
    ...bodyStyle,
    sno: index + 1,
    motorBodyType: bodyStyle?.bodyType?.name,
    createdAt: new Date(bodyStyle.createdAt).toLocaleDateString("en-GB"),
  }));

  const handlePaginationChange = (page) => {
    dispatch(setCurrentPage(page));
  };

  const handleRowsPerPageChange = (rowsPerPage) => {
    dispatch(setRowsPerPage(rowsPerPage));
    dispatch(setCurrentPage(1));
  };

  const handleEdit = (row) => {
    navigate(`/motor-master/edit-bodyStyle/${row?.id}`);
  };

  const handleView = (row) => {
    navigate(`/motor-master/view-bodyStyle/${row?.id}`);
  };

  const handleDelete = async (row) => {
    try {
      const response = await deleteData(`/reference/motor/bodystyle-delete`, {
        ids: [row?.id],
      });
      setIsRefresh(!isRefresh);
      if (response?.success) {
        showSnackbar(response?.message,"success");
      } else {
        showSnackbar(response?.message,"error");
      }
    } catch (error) {
      console.error("Error deleting data:", error);
      showSnackbar(error?.response?.data?.message, "error");
    }
  };

  const actions = {
    edit: true,
    view: true,
    delete: true,
    onEdit: (row) => handleEdit(row),
    onView: (row) => handleView(row),
    onDelete: (row) => handleDelete(row),
  };

  const handleAddNewBodyStyle = () => {
    navigate("/motor-master/add-bodyStyle");
  };

  const handleSearch = (value) => {
    setSearch(value);
  };
  return (
    <DashboardLayout>
      <CardHeader
        title={
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            spacing={2}
          >
            <Stack direction="column" spacing={0}>
              <MaterialTypography variant="h5" fontWeight="medium">
                Motor Body Style management list
              </MaterialTypography>
              <MaterialTypography variant="caption" color="text">
                List of motor body styles that you can get details of motor body
                style.
              </MaterialTypography>
            </Stack>

            <Stack direction="row" spacing={1}>
              <MaterialButton
                variant="gradient"
                color="dark"
                size="small"
                startIcon={<FilterAlt />}
                onClick={(event) => setAnchorEl(event.currentTarget)}
              >
                Filter
              </MaterialButton>
              <MaterialButton
                variant="gradient"
                color="dark"
                size="small"
                startIcon={<FileUpload />}
                onClick={() => setOpenUploadFileDialog(true)}
              >
                Bulk Upload
              </MaterialButton>
              <MaterialButton
                variant="gradient"
                color="dark"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => handleAddNewBodyStyle()}
              >
                Add Body Style
              </MaterialButton>
            </Stack>
          </Stack>
        }
      />
      <Card sx={{ mx: 1 }}>
        <Table
          columns={columns}
          data={rows}
          actions={actions}
          pagination={{
            totalPages,
            currentPage,
            onPageChange: (page) => handlePaginationChange(page),
            rowsPerPage,
            onRowsPerPageChange: (rowsPerPage) =>
              handleRowsPerPageChange(rowsPerPage),
            rowsPerPageOptions: [5, 10, 15, 20],
          }}
          getRowKey={(row) => row.id}
          className="custom-table"
          noDataLabel={"No motor body style data found"}
          handleSearch={handleSearch}
        />
      </Card>
      <BulkUpload
        open={openUploadFileDialog}
        setOpen={setOpenUploadFileDialog}
        endPoint="/reference/master/bulkUpload/body-styles"
        fetchList={fetchBodyStyleData}
      />
      <BodyStyleListFilter
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        filterData={filterData}
        setFilterData={setFilterData}
        onApply={() => {
          setAnchorEl(null); // close popup
          fetchBodyStyleData(); // call API after filter applied
        }}
      />
    </DashboardLayout>
  );
};

export default MotorBodyStyleList;
